<template>
  <div>
    <card :title="$t('gc-software:updateGateway')"
          :disabled="!$store.getters.bscFeature(gateway, 'update')"
    >
      <div slot="card-body">
        <dropzone id="softwareUpdateFormDropzone"
                  :url="url"
                  :start-upload.sync="startUpload"
                  icon="icon-file"
                  accepted-files=".tar.gz, .tgz, .tar, .ipk"
                  @dropzone-file-added="fileAdded"
                  @dropzone-file-removed="fileRemoved"
                  @dropzone-success="executeUpdateSucceed"
                  @dropzone-error="executeUpdateFailed"
        />

        <div class="form-row mt-3">
          <div class="form-group col-md-6 mb-0">
            <label for="applyTime">{{ $t('gc-software:applyTime') | capitalize }}</label>
            <select id="applyTime" v-model="applyTime" class="form-control">
              <option value="now">{{ $t('gc-software:now') | capitalize }}</option>
              <option value="planned">{{ $t('gc-software:planned') | capitalize }}</option>
            </select>
          </div>

          <div v-show="applyTime==='planned'" class="form-group col-md-6 mb-0 mt-sm-3 mt-md-0">
            <label for="applyDate">{{ $t('gc-software:applyDate') | capitalize }} *</label>
            <div class="input-group">
              <flat-pickr id="applyDate"
                          v-model="applyDate"
                          v-validate="{required: applyTime==='planned'}"
                          :config="fpConfig"
                          :data-vv-as="$t('gc-software:applyDate')"
                          :class="{ 'is-invalid': errors.has('applyDate') }"
                          name="applyDate"
                          class="form-control"
                          data-vv-value-path="innerValue"
              />
              <div class="input-group-append">
                <button id="resetDateButton" class="btn btn-flat btn-sm" type="button" @click="applyDate = null"><i class="icon-remove" /></button>
              </div>
              <div class="invalid-feedback">{{ errors.first('applyDate') }}</div>
            </div>
          </div>
        </div>
      </div>

      <div slot="card-footer">
        <button id="sendButton" :disabled="disabled" class="btn btn-primary" @click="confirmUpdate">{{ $t('gc-software:send') | capitalize }}</button>
      </div>
    </card>

    <confirm id="confirmUpdate"
             v-model="showConfirmUpdate"
             :message="$t('gc-software:confirmUpdate', { gateway: gateway.eui })"
             @confirm:validate="executeUpdate"
    />
  </div>
</template>

<script>
import { pull, isEmpty } from 'lodash-es'
import { mapGetters } from 'vuex'
import eventBus from '@/modules/eventBus'
import formatDate from 'vue-kerlink/dist/filters/date'

export default {
  components: { },

  props: {
    eui: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      files: [],
      url: '',
      applyTime: 'now',
      applyDate: null,

      showConfirmUpdate: false,
      startUpload: false,

      // In data to be modified if needed
      fpConfig: {
        wrap: true,
        enableTime: true,
        altInput: true,
        altFormat: 'Y/m/d H:i',
        dateFormat: 'U',
        time_24hr: true,
        minDate: Date.now()
      }
    }
  },

  computed: {
    ...mapGetters(['gateway']),

    disabled () {
      return this.files.length === 0
    }
  },

  watch: {
    eui () {
      // Reset data, and get data from new gateway
      this.reset()
    }
  },

  methods: {
    reset () {
      this.files = []
      this.url = ''
      this.applyTime = 'now'
      this.applyDate = null
    },

    fileAdded (file) {
      this.files.push(file)
    },

    fileRemoved (file) {
      pull(this.files, file)
    },

    async confirmUpdate () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      this.showConfirmUpdate = true
    },

    executeUpdate () {
      if (isEmpty(this.gateway)) return

      if (!this.gateway.connection) {
        eventBus.$emit('message-info', this.$t('gc-software:updateErrorNotConnected'))
        return
      }

      this.url = this.$utils.getLinkHref(this.$store.getters.application, 'updateGatewaysFirmware') + '?euis=' + this.gateway.eui
      if (this.applyTime === 'planned') {
        this.url += '&when=' + this.applyDate * 1000
      }

      this.startUpload = true
    },

    executeUpdateSucceed () {
      if (this.applyTime === 'planned') {
        eventBus.$emit('message-success', this.$t('gc-software:updatePlanned', { date: formatDate(this.applyDate) }))
      } else {
        eventBus.$emit('message-success', this.$t('gc-software:updateStarted'))
      }

      eventBus.$emit('gateway-update-started')
      this.reset()
    },

    executeUpdateFailed (file) {
      eventBus.$emit('message-error', this.$t('gc-software:updateError'))
    }
  }
}
</script>
